import {
  CallEnd as CallEndIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Peer from 'simple-peer';
import { db } from '../firebase';

interface VideoCallProps {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  currentUserId: string;
  currentUserName?: string; // Arayan kişinin adı (bildirim için gerekli)
  otherUserId: string;
  otherUserName: string;
  isInitiator: boolean;
}

const VideoCall: React.FC<VideoCallProps> = ({
  open,
  onClose,
  conversationId,
  currentUserId,
  currentUserName,
  otherUserId,
  otherUserName,
  isInitiator,
}) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [callAccepted, setCallAccepted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);

  const myVideo = useRef<HTMLVideoElement>(null);
  const userVideo = useRef<HTMLVideoElement>(null);
  const connectionRef = useRef<Peer.Instance | null>(null);
  const callDocRef = useRef<string | null>(null);

  const cleanupCall = useCallback(async () => {
    if (connectionRef.current) {
      connectionRef.current.destroy();
      connectionRef.current = null;
    }

    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }

    // Firestore'daki arama dokümanını güncelle
    if (callDocRef.current) {
      try {
        await updateDoc(doc(db, 'videoCalls', callDocRef.current), {
          status: 'ended',
        });

        // 10 saniye sonra dokümanı sil
        setTimeout(async () => {
          if (callDocRef.current) {
            await deleteDoc(doc(db, 'videoCalls', callDocRef.current));
          }
        }, 10000);
      } catch (error) {
        console.error('Arama temizleme hatası:', error);
      }
    }
  }, [stream]);

  const endCall = useCallback(() => {
    cleanupCall();
    onClose();
  }, [cleanupCall, onClose]);

  const createCall = useCallback(
    async (currentStream: MediaStream) => {
      try {
        setIsConnecting(true);

        // Firestore'da arama dokümanı oluştur
        const callDoc = await addDoc(collection(db, 'videoCalls'), {
          conversationId,
          from: currentUserId,
          fromName: currentUserName || 'Arayan',
          to: otherUserId,
          status: 'calling',
          timestamp: serverTimestamp(),
        });

        callDocRef.current = callDoc.id;

        // WebRTC peer bağlantısı oluştur
        const peer = new Peer({
          initiator: true,
          trickle: false,
          stream: currentStream,
        });

        peer.on('signal', async (data: any) => {
          await updateDoc(doc(db, 'videoCalls', callDoc.id), {
            offer: JSON.stringify(data),
          });
        });

        peer.on('stream', (remoteStream: MediaStream) => {
          if (userVideo.current) {
            userVideo.current.srcObject = remoteStream;
          }
          setCallAccepted(true);
          setIsConnecting(false);
        });

        connectionRef.current = peer;

        const unsubscribe = onSnapshot(
          doc(db, 'videoCalls', callDoc.id),
          (snapshot) => {
            const data = snapshot.data();
            if (data?.answer && !callAccepted) {
              const answer = JSON.parse(data.answer);
              peer.signal(answer);
            }
            if (data?.status === 'ended') {
              endCall();
            }
          }
        );

        return unsubscribe;
      } catch (error) {
        console.error('Arama oluşturma hatası:', error);
        setIsConnecting(false);
      }
    },
    [
      conversationId,
      currentUserId,
      currentUserName,
      otherUserId,
      callAccepted,
      endCall,
    ]
  );

  const answerCall = useCallback(
    async (currentStream: MediaStream) => {
      try {
        setIsConnecting(true);

        const callsQuery = collection(db, 'videoCalls');
        const unsubscribe = onSnapshot(callsQuery, (snapshot) => {
          snapshot.docChanges().forEach(async (change) => {
            if (change.type === 'added') {
              const data = change.doc.data();
              if (
                data.to === currentUserId &&
                data.from === otherUserId &&
                data.status === 'calling' &&
                data.offer
              ) {
                callDocRef.current = change.doc.id;

                const peer = new Peer({
                  initiator: false,
                  trickle: false,
                  stream: currentStream,
                });

                peer.on('signal', async (data: any) => {
                  await updateDoc(doc(db, 'videoCalls', change.doc.id), {
                    answer: JSON.stringify(data),
                    status: 'connected',
                  });
                });

                peer.on('stream', (remoteStream: MediaStream) => {
                  if (userVideo.current) {
                    userVideo.current.srcObject = remoteStream;
                  }
                  setCallAccepted(true);
                  setIsConnecting(false);
                });

                connectionRef.current = peer;

                const offer = JSON.parse(data.offer);
                peer.signal(offer);
              }
            }
          });
        });

        return unsubscribe;
      } catch (error) {
        console.error('Arama cevaplama hatası:', error);
        setIsConnecting(false);
      }
    },
    [currentUserId, otherUserId]
  );

  const initializeCall = useCallback(async () => {
    try {
      const currentStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      setStream(currentStream);

      if (myVideo.current) {
        myVideo.current.srcObject = currentStream;
      }

      if (isInitiator) {
        await createCall(currentStream);
      } else {
        await answerCall(currentStream);
      }
    } catch (error) {
      console.error('Media erişim hatası:', error);
      alert(
        'Kamera veya mikrofona erişim sağlanamadı. Lütfen izinleri kontrol edin.'
      );
      onClose();
    }
  }, [isInitiator, createCall, answerCall, onClose]);

  useEffect(() => {
    if (open) {
      initializeCall();
    }

    return () => {
      cleanupCall();
    };
  }, [open, initializeCall, cleanupCall]);

  const toggleMute = () => {
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          endCall();
        }
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #263238 0%, #37474F 100%)',
          minHeight: '80vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative', minHeight: '80vh' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: '80vh',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(0, 0, 0, 0.3)',
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              {otherUserName}
            </Typography>
            {isConnecting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                <Typography variant="body2" sx={{ color: 'white' }}>
                  Bağlanıyor...
                </Typography>
              </Box>
            )}
          </Box>

          {/* Video Area */}
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#000000',
            }}
          >
            {/* Remote Video (Karşı taraf) */}
            <video
              ref={userVideo}
              playsInline
              autoPlay
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: callAccepted ? 'block' : 'none',
              }}
            />

            {/* Waiting State */}
            {!callAccepted && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: 'white',
                }}
              >
                <CircularProgress size={60} sx={{ color: '#43A047', mb: 2 }} />
                <Typography variant="h6">
                  {isInitiator ? 'Aranıyor...' : 'Arama bekleniyor...'}
                </Typography>
              </Box>
            )}

            {/* My Video (Küçük pencere) */}
            <Paper
              elevation={8}
              sx={{
                position: 'absolute',
                bottom: 20,
                right: 20,
                width: 200,
                height: 150,
                borderRadius: 2,
                overflow: 'hidden',
                border: '2px solid #43A047',
              }}
            >
              <video
                ref={myVideo}
                playsInline
                muted
                autoPlay
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: 'scaleX(-1)', // Mirror effect
                }}
              />
              {isVideoOff && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#263238',
                  }}
                >
                  <VideocamOffIcon sx={{ fontSize: 48, color: 'white' }} />
                </Box>
              )}
            </Paper>
          </Box>

          {/* Controls */}
          <Box
            sx={{
              p: 3,
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              background: 'rgba(0, 0, 0, 0.3)',
            }}
          >
            <IconButton
              onClick={toggleMute}
              sx={{
                bgcolor: isMuted ? '#F44336' : 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                width: 56,
                height: 56,
                '&:hover': {
                  bgcolor: isMuted ? '#D32F2F' : 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              {isMuted ? <MicOffIcon /> : <MicIcon />}
            </IconButton>

            <IconButton
              onClick={toggleVideo}
              sx={{
                bgcolor: isVideoOff ? '#F44336' : 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                width: 56,
                height: 56,
                '&:hover': {
                  bgcolor: isVideoOff ? '#D32F2F' : 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              {isVideoOff ? <VideocamOffIcon /> : <VideocamIcon />}
            </IconButton>

            <IconButton
              onClick={endCall}
              sx={{
                bgcolor: '#F44336',
                color: 'white',
                width: 56,
                height: 56,
                '&:hover': {
                  bgcolor: '#D32F2F',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <CallEndIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VideoCall;
